import React, { useEffect, useState } from "react";
import * as PR from "../../prime-modules/index";
import { Formik, Form } from "formik";
import "./index.scss";
import Input from "../../components/input";
import { createPrepaidVoucher } from "../../utils/schema";
import { useMutation, useQuery } from "react-query";
import {
  createBatch,
  searchPrepaidVoucherApi,
} from "../../services/affiliateApi";
import { serializeParams, setPrice } from "../../utils/reuse";
import RoamingColumn from "./RoamingInfo.jsx";
import { useSelector } from "react-redux";
import { globalConfig } from "../../constants.js";

const CreatePrepaidVoucher = ({
  allCountries,
  visible,
  setVisible,
  toast,
  getBatch,
  uniqueRegions,
}) => {
  
  const {affiliate} = useSelector(state=>state.language?.activelang);

  const [queryParams, setQueryParams] = useState("");
  const [filterCountries, setFilterCountries] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    setFilterCountries(allCountries);
  }, [allCountries]);

  const regionFilterHandler = (setFieldValue, selectedRegion) => {
    setFieldValue("region", selectedRegion);
    setFieldValue("country", "");
  
    const countriesByRegion = allCountries?.filter((c) => c.region === selectedRegion);
    setFilterCountries(countriesByRegion);
  };
  
  const selectBox = (rowData) => {
    return (
      <div className="card flex justify-content-center">
        <PR.Checkbox
          inputId={rowData?.name}
          checked={rowData?.checked || false}
          onChange={e => {
            setBundles(prevState =>
              prevState.map(bundle =>
                bundle.name === rowData?.name
                  ? { ...bundle, checked: e.checked, count: e.checked ? bundle.count : "" }
                  : bundle
              )
            );
        }}
        />
      </div>
    );
  };

 
  const handleVoucherChange = (rowData, newCount) => {
    setBundles(prevState =>
      prevState.map(bundle =>
        bundle.name === rowData?.name
          ? { ...bundle, count: newCount }
          : bundle
      )
    );
  };
  const vouchers = (rowData, {rowIndex}) => {
    return (
      <div className="card">
        <PR.InputText
          type="text"
          id={`vouchers${rowIndex}`}
          inputMode="numeric"
          maxLength={4}
          value={rowData?.count}
          onChange={(e) => {
            const rawValue = e.target.value.replace(/[^0-9]/g, "");
            const parsedValue = parseInt(rawValue, 10);
        
            // Allow clearing the input
            if (rawValue === "") {
              handleVoucherChange(rowData, "");
              return;
            }
        
            // Prevent leading zeros
            if (parsedValue === 0) {
              handleVoucherChange(rowData, 1);
              return;
            }
        
            // Ensure max 4 digits and max value of 9999
            const trimmedValue = rawValue.slice(0, 4);
            const validValue = Math.min(parseInt(trimmedValue, 10), 9999);
        
            handleVoucherChange(rowData, validValue);
          }}
          disabled={!rowData?.checked}
          placeholder="-"
          className="vouchers-input"
        />
      </div>
    );
  };
  const { isLoading, refetch } = useQuery(["SearchVoucher", queryParams], {
    enabled: false,
    queryFn: () => searchPrepaidVoucherApi(queryParams),
    onSuccess: (data) => {
      let bundelData = [];
      if(data?.length){
        bundelData = data.map(d => {
         d.count = '';
         d.checked = false;
         return d;
        })
      }
      setBundles([...bundelData]);
    },
    onError: (error) => {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        life:globalConfig?.errorToastDisplayTime,
        detail:
          error?.message || "An error occurred while reserving the batch.",
      });
    },
  });

  const { mutate } = useMutation(createBatch);

  const handleSubmit = (resetForm, values) => {
    const selectedVoucherData = bundles.filter(b => b.checked).map(b => ({ discountBundle: b.name, count:  b.count }));
    const hasInvalidVoucherCount = selectedVoucherData?.some(
      (bundle) => bundle?.count === ""
    );

    if (hasInvalidVoucherCount) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Voucher count cannot be 0 in selected bundles",
      });
      return; // Prevent API call
    }
    const currentDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(currentDate.getFullYear() + 3);
    const formData = {
      batchName: values.batchName,
      voucherCount: parseInt(values.voucherCount, 10),
      startTs: currentDate.toISOString(),
      endTs: endDate.toISOString(),
      discounts: selectedVoucherData,
      maxNrOfUses: 1,
    };

    const totalSelectedCounts = selectedVoucherData?.reduce(
      (sum, b) => sum + (b.count || 0),
      0
    );
    if (totalSelectedCounts !== +values?.voucherCount) {
      toast.current.show({
        severity: "warn",
        summary: "Total mismatch",
        detail: "Total voucher count does not match selected vouchers.",
        life: globalConfig?.toastDisplayTime,
      });
      return;
    }
    mutate(formData,{
      onSuccess: (o) => {
          getBatch();
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Created Batch successfully",
          });
          setVisible(false)
          setBundles([])
          resetForm();
          setFilterCountries(allCountries)
      },
      onError: (error) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          life:globalConfig?.errorToastDisplayTime,
          detail:
            error?.message || "An error occurred while reserving the batch.",
        });
      },
    });

  };

  const onHideHandler = () => {
    setBundles([]);
    setVisible(false);
  };

  const searchBundle =(values)=>{
    const defaultFilters = {
      country: values?.country ?? "",
      region: values.region ?? "",
      sortingOrder: "asc",
      sortingField: "providerprice",
    };
    const queryParams = serializeParams(defaultFilters);
    setQueryParams(queryParams);

    setTimeout(() => {
      refetch();
    }, 100);
  }
  const getCount = () => bundles.filter(b => b.checked).length;

  return (
    <PR.Dialog
      header={affiliate?.AFFNL0067 || "Create Prepaid Voucher(s)"}
      visible={visible}
      draggable={false}
      blockScroll={true}
      onHide={onHideHandler}
      className="create-voucher-dailog"
    >
      <Formik
        initialValues={{
          batchName: "",
          voucherCount: null,
          region: null,
          country: "GB",
        }}
        validationSchema={createPrepaidVoucher}
        onSubmit={(values,{resetForm})=>{
          handleSubmit(resetForm, values)
        }}
      >
        {({ values, setFieldValue, resetForm }) => (
          <Form>
            <div className="grid">
              <div className="col-12 pb-1">
                <label htmlFor="batchName">
                  {affiliate?.AFFNL0068 || "Enter Batch Name & Voucher Count"}
                </label>
              </div>
              <div className="col-12 md:col-6">
                <Input
                  label=""
                  name="batchName"
                  id="batchName"
                  value={values?.batchName ?? ''}
                  type="text"
                  aria-describedby="batchName"
                  placeholder={affiliate?.AFFNL0069 || "Name of the batch"}
                />
              </div>
              <div className="col-12 md:col-6">
                <Input
                  label=""
                  name="voucherCount"
                  id="voucherCount"
                  value={values?.voucherCount ?? ""}
                  inputMode="numeric"
                  maxLength={4}
                  onInput={(e) => {
                    let rawValue = e.target.value.replace(/\D/g, ""); // Remove all non-digits
                
                    if (rawValue.length > 4) {
                      rawValue = rawValue.slice(0, 4); // Limit to 4 digits
                    }
                
                    let numericValue = parseInt(rawValue, 10);
                
                    if (isNaN(numericValue)) {
                      e.target.value = ""; // Set empty if invalid
                    } else if (numericValue === 0) {
                      e.target.value = "1"; // Correct 0 to 1
                    } else if (numericValue > 9999) {
                      e.target.value = "9999"; // Limit max value
                    } else {
                      e.target.value = numericValue.toString(); // Normal case
                    }
                  }}
                  aria-describedby="voucherCount"
                  placeholder={affiliate?.AFFNL0070 || "Total number of vouchers"}
                />
              </div>
            </div>

            <div className="grid mt-2">
              <div className="col-12 pb-1">
                <label>{affiliate?.AFFNL0071 || "Select region or country"}</label>
              </div>

              <div className="col-12 md:col-5">
                <PR.Dropdown
                  name="region"
                  value={values.region}
                  onChange={(e) => {
                    const selectedRegion = e.value;
                    if (!selectedRegion) {
                      setFieldValue("region", "");
                      setFieldValue("country", "GB");
                      setFilterCountries(allCountries); 
                    } else {
                      regionFilterHandler(setFieldValue, selectedRegion);
                    }
                  }}
                  filter
                  options={uniqueRegions}
                  optionLabel="label"
                  placeholder={affiliate?.AFFNL0072 || "Select region"}
                  className="w-full"
                  showFilterClear
                  {...(values.region ? { showClear: true } : {})}
                />
              </div>

              <div className="col-12 md:col-5">
                <PR.Dropdown
                  name="country"
                  value={values.country}
                  onChange={(e) => {
                    setFieldValue("country", e.value);
                  }}
                  options={filterCountries}
                  optionLabel="name"
                  optionValue="iso"
                  filter
                  placeholder={affiliate?.AFFNL0073 || "Select country"}
                  className="w-full"
                />
              </div>

              <div className="col-12 md:col-2">
                <PR.Button
                type="button"
                onClick={()=>searchBundle(values)}
                  label={affiliate?.AFFNL0074 || "Search"}
                  className="Search-button minWidth"
                />
              </div>
            </div>

            <div className="grid mt-2">
              <div className="col-12 pb-1">
                <label>{affiliate?.AFFNL0075 || "Select bundle(s)"}</label>
              </div>
              <div className="col-12">
                <PR.DataTable
                  dataKey="name"
                  editMode="row"
                  className="bundles-datatable create-prepaid-dailog-datatable"
                  loading={isLoading}
                  value={bundles}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  emptyMessage={
                    <span className="text-gray-500">{affiliate?.AFFNL0076 || "No records found"}</span>
                  }
                  onSort={(e) => {
                    setSortField(e.sortField);
                    setSortOrder(e.sortOrder);
                }}
                >
                  {!!bundles && bundles.length === 0 && (
                    <PR.Column colSpan={1} style={{ textAlign: "center" }}>
                      {affiliate?.AFFNL0076 || "No records found"}
                    </PR.Column>
                  )}
                  <PR.Column
                  body={(rowData, meta) => selectBox(rowData, meta)}                
                   header={affiliate?.AFFNL0077 || "Select"}
                    className="select-column"
                    headerClassName="header-select-column"
                  />
                  <PR.Column field="name" header={affiliate?.AFFNL0078 || "Name of the bundle"} />
                  <PR.Column field="dataAmount" header={affiliate?.AFFNL0079 || "Data"} sortable body={(rowData) => (rowData.dataAmountForDisplay)} />
                  <PR.Column
                    body={(rowData) => `${rowData?.durationDays} days`}
                    header={affiliate?.AFFNL0080 || "Duration"}
                  />
                  <PR.Column
                    field="bundle.roamingEnabled" // Accessing roaming information
                    header={affiliate?.AFFNL0004 || "Roaming"}
                    body={(data) => <RoamingColumn data={data} />}
                    style={{width:"12%"}}
                  />
                  <PR.Column field="price" body={(voucher)=>setPrice(voucher?.price)} sortable header={affiliate?.AFFNL0081 || "Price"} />
                  <PR.Column
                    body={vouchers}
                    header={affiliate?.AFFNL0082 || "No. of vouchers"}
                    className="last-column"
                  />
                </PR.DataTable>
              </div>

              <div className="col-12 flex gap-3 align-items-center justify-content-end mt-2">
                <PR.Button
                  type="reset"
                  label={affiliate?.AFFNL0083 || "Reset"}
                  onClick={() => {
                    resetForm();
                    setBundles([])
                    setFilterCountries(allCountries);
                  }}
                  className="Search-button reset"
                />
                <PR.Button
                  type="submit"
                  label={affiliate?.AFFNL0084 || "Create"}
                  disabled={getCount() === 0 || isLoading}
                  className="Search-button create"
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PR.Dialog>
  );
};

export default CreatePrepaidVoucher;
